import { Link } from 'react-router-dom';
import { Card, ProgressBar, ListGroup, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React from 'react';

import InfoTooltip from './InfoTooltip';

import './ProfileCompletionCard.css';

const STEP_CONFIG = {
    name: {
        label: 'Add your name',
        description: 'Let the community know who you are.',
        link: '/profile/edit',
        linkText: 'Add Name',
    },
    bio: {
        label: 'Write a short bio',
        description:
            'Tell others about yourself and your sustainability interests.',
        link: '/profile/edit',
        linkText: 'Add Bio',
    },
    photo: {
        label: 'Upload a profile photo',
        description: 'A photo helps others recognize you.',
        link: '/profile',
        linkText: 'Upload Photo',
    },
    interests: {
        label: 'Add your interests',
        description:
            'Help us personalize your experience with relevant topics.',
        link: '/profile/edit',
        linkText: 'Add Interests',
    },
    tour: {
        label: 'Take the platform tour',
        description: 'A quick walkthrough of the Sustainability Hub features.',
        action: 'tour',
        linkText: 'Start Tour',
    },
};

function getProgressVariant(percentage) {
    if (percentage < 40) return 'danger';
    if (percentage < 70) return 'warning';
    return 'success';
}

/**
 * Compute profile completion from a user profile object.
 * Returns { percentage, missing } where missing is an array of step keys.
 */
export function computeProfileCompletion(profile) {
    if (!profile) return { percentage: 0, missing: Object.keys(STEP_CONFIG) };

    const missing = [];

    if (!profile.name?.first || !profile.name?.last) missing.push('name');
    if (!profile.bio) missing.push('bio');
    if (!profile.profilePicture) missing.push('photo');
    if (!profile.interests?.length) missing.push('interests');
    if (!profile.hasSeenTour) missing.push('tour');

    const total = Object.keys(STEP_CONFIG).length;
    const completed = total - missing.length;
    const percentage = Math.round((completed / total) * 100);

    return { percentage, missing };
}

export default function ProfileCompletionCard({
    profileCompletion,
    onStartTour,
}) {
    if (!profileCompletion || profileCompletion.percentage >= 100) {
        return null;
    }

    const { percentage, missing } = profileCompletion;

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    {/* Outside the h2: an aria-label inside a heading gets
                        folded into the heading's accessible name. */}
                    <div className="d-flex align-items-center">
                        <h2 className="h6 mb-0 fw-bold">
                            Complete Your Profile
                        </h2>
                        <InfoTooltip text="Five steps make up a complete profile and each counts equally, so finishing one adds 20%. Only the steps you have left are listed below." />
                    </div>
                    <span className="text-muted small">{percentage}%</span>
                </div>
                {/* Nest a child ProgressBar so the accessible name lands on
                    the inner role="progressbar" element (react-bootstrap only
                    spreads arbitrary props, including aria-label, onto a child
                    bar, not the outer wrapper). */}
                <ProgressBar className="mb-3 progress-thin">
                    <ProgressBar
                        now={percentage}
                        variant={getProgressVariant(percentage)}
                        aria-label={`Profile completion: ${percentage} percent`}
                    />
                </ProgressBar>
                <ListGroup variant="flush">
                    {missing.map((key) => {
                        const step = STEP_CONFIG[key];
                        if (!step) return null;

                        return (
                            <ListGroup.Item
                                key={key}
                                className="d-flex justify-content-between align-items-center px-0 border-start-0 border-end-0"
                            >
                                <div>
                                    <div className="small fw-medium">
                                        {step.label}
                                    </div>
                                    <div className="text-muted small">
                                        {step.description}
                                    </div>
                                </div>
                                {step.action === 'tour' ? (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="rounded-pill flex-shrink-0 ms-3"
                                        onClick={onStartTour}
                                    >
                                        {step.linkText}
                                    </Button>
                                ) : (
                                    <Button
                                        as={Link}
                                        to={step.link}
                                        variant="outline-primary"
                                        size="sm"
                                        className="rounded-pill flex-shrink-0 ms-3"
                                    >
                                        {step.linkText}
                                    </Button>
                                )}
                            </ListGroup.Item>
                        );
                    })}
                </ListGroup>
            </Card.Body>
        </Card>
    );
}

ProfileCompletionCard.propTypes = {
    profileCompletion: PropTypes.shape({
        percentage: PropTypes.number.isRequired,
        missing: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
    onStartTour: PropTypes.func.isRequired,
};
